import axios, { AxiosInstance, AxiosError, InternalAxiosRequestConfig } from 'axios';
import config from '../../config/config';
import { auth } from '../../firebase';
import { getIdToken } from 'firebase/auth';
import { getSensitiveData, saveSensitiveData, removeSensitiveData } from '../../utils/secureStorage';

// Retry config — only safe for idempotent GET requests
const RETRY_MAX = 2;
const RETRY_BASE_DELAY_MS = 1000; // 1 s, 2 s (exponential)

type RetryableRequest = InternalAxiosRequestConfig & { _retryCount?: number };

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

const shouldRetry = (request: RetryableRequest): boolean =>
  request?.method?.toUpperCase() === 'GET' &&
  (request._retryCount ?? 0) < RETRY_MAX;

const retryRequest = async (request: RetryableRequest) => {
  const retryCount = request._retryCount ?? 0;
  request._retryCount = retryCount + 1;
  await sleep(RETRY_BASE_DELAY_MS * Math.pow(2, retryCount)); // 1s, 2s
  return apiClient(request);
};

export const API_BASE_URL = config.API_BASE_URL;

export const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: config.API_TIMEOUT,
});

apiClient.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    if (config.data instanceof FormData) {
      delete config.headers['Content-Type'];
    }
    const token = await getSensitiveData('authToken');
    
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    return config;
  },
  (error: AxiosError) => {
    return Promise.reject(error);
  }
);

// Track in-flight token refresh to avoid parallel refresh calls.
// Assigned synchronously (no `await` between check and set) so concurrent
// 401s racing through the interceptor can't all slip past the guard before
// the first one claims it.
let refreshPromise: Promise<string | null> | null = null;
let isRedirectingToLogin = false;

const redirectToLoginOnce = async () => {
  if (isRedirectingToLogin) {
    return;
  }

  isRedirectingToLogin = true;
  await removeSensitiveData('authToken');
  await removeSensitiveData('userId');

  if (globalThis.location.pathname !== '/') {
    globalThis.location.replace('/');
  }
};

apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean } & RetryableRequest;

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      if (!refreshPromise) {
        refreshPromise = (async () => {
          // Firebase may still be restoring a persisted session (e.g. right
          // after a full page reload) — `currentUser` can be transiently
          // null even though a valid session exists. Wait for the initial
          // state check to settle before treating this as "logged out".
          await auth.authStateReady();
          const firebaseUser = auth.currentUser;

          if (!firebaseUser) {
            await redirectToLoginOnce();
            return null;
          }

          try {
            const freshToken = await getIdToken(firebaseUser, true);
            await saveSensitiveData('authToken', freshToken);
            return freshToken;
          } catch {
            await redirectToLoginOnce();
            return null;
          }
        })().finally(() => {
          refreshPromise = null;
        });
      }

      const freshToken = await refreshPromise;
      if (!freshToken) {
        throw error;
      }

      originalRequest.headers.Authorization = `Bearer ${freshToken}`;
      return apiClient(originalRequest);
    }

    if (error.response) {
      switch (error.response.status) {
        case 403:
          console.error('Forbidden - You do not have permission');
          break;
        case 404:
          console.error('Resource not found');
          break;
        case 500:
          console.error('Internal server error');
          break;
        default:
          console.error('An error occurred:', error.response.data);
      }
    } else if (error.request) {
      // No response received — could be offline, timeout, or captive portal.
      // Auto-retry GET requests up to RETRY_MAX times with exponential backoff.
      if (shouldRetry(originalRequest)) {
        return retryRequest(originalRequest);
      }
      // All retries exhausted (or non-GET) — notify the global OfflineBanner
      globalThis.dispatchEvent(new CustomEvent('app:network-error'));
    } else {
      console.error('Error setting up request:', error.message);
    }
    
    throw error;
  }
);

export default apiClient;
